import React, { useMemo, useState } from 'react';
import { NtIcon, NT_COLORS } from './NotionUI';
import { useLanguage } from '../i18n/LanguageContext';

const DAY_MS = 86400000;
const toDay = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};
const toIso = (day) => new Date(day * DAY_MS).toISOString().slice(0, 10);
const todayDay = () => {
  const now = new Date();
  return Math.round(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS);
};
const MAX_RANGE_DAYS = 62;

/**
 * Vista calendario (mese): ogni attività con una data compare nel giorno
 * (o nei giorni, per un intervallo). Si trascina una attività su un altro
 * giorno per spostarla; "+" in un giorno crea una nuova attività con quella data.
 */
function ProjectCalendar({ tasks, dateColumn, statusColumn, color, onOpen, onMove, onAdd }) {
  const { t, lang } = useLanguage();
  const today = todayDay();
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [drag, setDrag] = useState(null); // {id, from}
  const [over, setOver] = useState(null);

  const key = String(dateColumn.id);
  const isRange = dateColumn.type === 'daterange';

  // Giorni occupati da ogni attività; "senza data" per le altre.
  const { byDay, unscheduled } = useMemo(() => {
    const map = {};
    const none = [];
    tasks.forEach((task) => {
      const raw = task.data[key];
      let start = null;
      let end = null;
      if (isRange) {
        if (raw && (raw.start || raw.end)) {
          start = toDay(raw.start || raw.end);
          end = Math.max(start, toDay(raw.end || raw.start));
        }
      } else if (raw) {
        start = toDay(raw);
        end = start;
      }
      if (start === null) {
        none.push(task);
        return;
      }
      for (let day = start; day <= Math.min(end, start + MAX_RANGE_DAYS); day += 1) {
        (map[day] = map[day] || []).push({ task, first: day === start, last: day === end });
      }
    });
    return { byDay: map, unscheduled: none };
  }, [tasks, key, isRange]);

  const firstOfMonth = Math.round(Date.UTC(cursor.y, cursor.m, 1) / DAY_MS);
  const weekday = (new Date(firstOfMonth * DAY_MS).getUTCDay() + 6) % 7; // lunedì = 0
  const gridStart = firstOfMonth - weekday;
  const cells = Array.from({ length: 42 }, (_, i) => gridStart + i);

  const monthLabel = new Date(firstOfMonth * DAY_MS).toLocaleDateString(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const weekdays = Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(2024, 0, 1 + i)).toLocaleDateString(lang, { weekday: 'short', timeZone: 'UTC' }));

  const shiftMonth = (delta) => setCursor((c) => {
    const d = new Date(Date.UTC(c.y, c.m + delta, 1));
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() };
  });

  const chipColor = (task) => {
    if (statusColumn) {
      const option = statusColumn.options.find((o) => o.value === task.data[String(statusColumn.id)]);
      if (option) return NT_COLORS[option.color] || NT_COLORS.grey;
    }
    return color;
  };

  const drop = (day) => {
    setOver(null);
    if (!drag) return;
    const delta = day - drag.from;
    const current = drag;
    setDrag(null);
    if (delta !== 0) onMove(current.id, delta, toIso);
  };

  return (
    <div className="cal">
      <div className="cal-head">
        <button type="button" className="nt-ghost-btn" onClick={() => shiftMonth(-1)} aria-label={t('projectView.prevMonth')}>‹</button>
        <button type="button" className="nt-ghost-btn" onClick={() => shiftMonth(1)} aria-label={t('projectView.nextMonth')}>›</button>
        <strong className="cal-title">{monthLabel}</strong>
        <button type="button" className="nt-ghost-btn" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }}>{t('projectView.today')}</button>
      </div>
      <div className="cal-grid">
        {weekdays.map((name) => <div key={name} className="cal-weekday">{name}</div>)}
        {cells.map((day) => {
          const date = new Date(day * DAY_MS);
          const inMonth = date.getUTCMonth() === cursor.m;
          const items = byDay[day] || [];
          return (
            <div
              key={day}
              className={'cal-day' + (inMonth ? '' : ' cal-day-out') + (day === today ? ' cal-day-today' : '') + (over === day ? ' cal-day-over' : '')}
              onDragOver={(e) => { e.preventDefault(); setOver(day); }}
              onDragLeave={() => setOver((cur) => (cur === day ? null : cur))}
              onDrop={(e) => { e.preventDefault(); drop(day); }}
            >
              <div className="cal-day-head">
                <span className="cal-day-num">{date.getUTCDate()}</span>
                <button type="button" className="pd-row-delete cal-add" aria-label={t('projectDetail.newTask')} title={t('projectDetail.newTask')} onClick={() => onAdd(toIso(day))}>+</button>
              </div>
              <div className="cal-items">
                {items.map(({ task, first }) => (
                  <button
                    key={task.id}
                    type="button"
                    className="cal-chip"
                    style={{ '--bar': chipColor(task) }}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(task.id)); setDrag({ id: task.id, from: day }); }}
                    onClick={() => onOpen(task)}
                    title={task.title}
                  >
                    {first ? (task.title || t('projectDetail.untitled')) : <span className="nt-muted">↳ {task.title}</span>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {unscheduled.length > 0 && (
        <div className="gantt-unscheduled">
          <div className="nt-muted gantt-unscheduled-title"><NtIcon name="calendar" /> {t('projectDetail.ganttUnscheduled', { count: unscheduled.length })}</div>
          {unscheduled.map((task) => (
            <button key={task.id} type="button" className="nt-ghost-btn" onClick={() => onOpen(task)}>{task.title || t('projectDetail.untitled')}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProjectCalendar;
